import * as React from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@align-cpf/uikit-icons';
import { Icon } from '../../../atoms';
import { Typography } from '../../../atoms/Typography';
import { accordionOptionVariants } from '../../../../animation';
import { createLocators } from '@u1/locators';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import useStyles from './AccordionOption.styles';

export interface IAccordionOptionProps {
  /**
   * Text displayed on the option button
   */
  label: string;
  /**
   * React element hidden under accordion option
   */
  children: React.ReactNode;
  /**
   * Triggers disabled state
   * @default false
   */
  disabled?: boolean;
  /**
   * Triggers opened state
   * @default false
   */
  opened?: boolean;
  /**
   * On click handler
   */
  onClick?(): unknown;
}

export const accordionLocators = createLocators({
  name: 'AccordionOption',
  componentLocators: [
    'root',
    'label',
    'content',
    'contentContainer',
    'labelTrigger',
  ],
});
const { locators } = accordionLocators();

const AccordionOption: React.FC<IAccordionOptionProps> = ({
  label,
  children,
  disabled,
  onClick,
  opened,
}) => {
  const classes = useStyles();

  return (
    <div
      {...locators.root({ isOpen: opened, disabled })}
      className={clsx(classes.root, {
        [classes.disabled]: disabled,
        [classes.opened]: opened,
      })}
    >
      <button
        {...locators.labelTrigger({ disabled })}
        className={classes.label}
        onClick={onClick}
        disabled={disabled}
        type="button"
      >
        <Typography
          locatorsOverrides={locators.label({ disabled })}
          as={'span'}
          variant={'heading01'}
          className={classes.labelText}
        >
          {label}
        </Typography>
        <Icon
          icon={opened ? ChevronUpIcon : ChevronDownIcon}
          className={classes.icon}
        />
      </button>
      <motion.div
        {...locators.contentContainer({ isOpen: opened })}
        initial="hidden"
        animate={opened ? 'visible' : 'hidden'}
        variants={accordionOptionVariants}
        transition={{ duration: 0.2 }}
        className={classes.content}
      >
        <Typography
          locatorsOverrides={locators.content()}
          as="div"
          variant={'heading01'}
          className={classes.contentText}
        >
          {children}
        </Typography>
      </motion.div>
    </div>
  );
};

export default AccordionOption;
